/**
 * Home-screen "Planner" section. ONE mode, no tabs: a compact week strip is
 * always on, sitting directly above the date band, and the day picked on it
 * alone drives the list below. Self-contained: owns its data hook (the real
 * api/resident-todos seam), so it slots into the mobile and desktop home views
 * unchanged.
 *
 * State model: `selectedDate` only. The visible week is always derived from it,
 * and ‹ › paging just moves selectedDate ±7 days. The way back to today is
 * today's own cell (or paging back to its week); the midnight carry below also
 * returns an untouched selection there on its own.
 *
 * Layout: two ways to bound the todo list, both keeping the week strip and the
 * day heading pinned while ONLY the list scrolls.
 *   `fill`            stretches the section from "as tall as its rows" to
 *                     "take the parent's remaining height" — desktop's
 *                     full-height planner column.
 *   `maxVisibleTodos` leaves the card intrinsically sized but stops the list
 *                     at N MEASURED row heights and scrolls past them — the
 *                     no-page-scroll mobile home's three-row planner. N is a
 *                     MAXIMUM: `squeezeBy` takes height back off it, down to
 *                     one whole row, so the planner yields before the page does;
 *                     `collapseList` (the page's last rung) drops the body
 *                     outright, and `onSqueezeCapacity` tells the page how much
 *                     each of those is worth so it can decide in one pass.
 * A ScrollView — not a FlatList — because the list is a single day's worth of
 * rows and virtualization would hide rows from the tree that the section's
 * tests walk.
 *
 * Two things stay pinned above that scroller so they can never scroll away:
 * the date band (which reads "· N due", or "· all done" once a populated day
 * is clear) and — when there is anything past due and a Planner to send the
 * tap to — a one-line overdue link. Whenever the list is scrollable a bottom
 * gradient fades it into the card, but only while it actually overflows and is
 * not already scrolled to the end.
 *
 * "Today" is a moving anchor, not a mount-time snapshot: home is the stack's
 * initial route and stays mounted for days, and both the date band and the
 * overdue cutoff would otherwise still be yesterday's after midnight.
 */
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  Platform,
  type LayoutChangeEvent,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from 'react-native';
import { Text } from '@/components/common/text';
import LinearGradient from 'react-native-linear-gradient';
import Icon from 'react-native-vector-icons/Ionicons';
import { GlassCard } from '../../GlassCard';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { SectionHeader } from '../../SectionHeader';
import { useThemeColors, useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useTypography } from '@/hooks/shared/useTypography';
import { useSpacing } from '@/hooks/shared/useSpacing';
import { useMinuteClock } from '@/hooks/shared/useMinuteClock';
import { useResidentTodos } from '@/hooks/residents/useResidentTodos';
import { hexToRgba } from '@/utils/shared/color';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import {
  addDays,
  formatDayHeading,
  getWeekDays,
  groupTodosByDateKey,
  sortTodos,
  toDateKey,
} from '@/utils/residents/todoCalendar';
import { formatTime, isLate, nextTimedTodo, nowLineIndex, toTimeKey } from '@/utils/residents/todoTime';
import type { EditScope, ResidentTodo } from '@/types/residents/todos';
import { WeekStrip } from './WeekStrip';
import {
  TodoRow,
  CIRCLE_REACH,
  TodoFormModal,
  insertNowLine,
  type TodoFormValues,
} from '@/components/residents/redesign/todoCalendar';

// The closest planner glyph the asset set has (admin/calendar.svg, currentColor
// strokes so it takes the muted ink below).
const CalendarIcon = RESIDENTS_SVG_COMPONENTS.CALENDAR;

/** Scroll-fade height on a roomy list. Deep enough to read as a fade, shallow
 *  enough not to wash out the row beneath it. */
const SCROLL_FADE_HEIGHT = 28;
/**
 * The most of the list viewport a fade may cover. The full depth is sized for
 * a three-row list, where it only touches the last row's bottom padding; on a
 * list squeezed to a single row it would sit on the bottom half of the only
 * row and veil its text. A third of a 57pt row is its 16pt bottom padding plus
 * a hair — the fade still reads as an edge and the title stays legible.
 */
const SCROLL_FADE_MAX_SHARE = 1 / 3;
/**
 * How far each fade reaches PAST the list viewport's edge. On web the viewport's
 * edges land on fractional pixels (row heights are measured unrounded, and the
 * chrome above the list sets in `normal` line-heights), and a fade flush with
 * the clip edge leaves that last device-pixel row only partly covered — a
 * hairline of undimmed row along the seam. One pixel past the edge covers it.
 * The overshoot paints card-on-card in the 16pt gaps around the list, so it
 * never shows; native snaps to whole pixels and is unaffected either way.
 */
const SCROLL_FADE_OVERSHOOT = 1;
/** Treat "within 8pt of the bottom" as the end, so the fade is gone before the
 *  last row is, and sub-pixel content heights can't leave it stuck on. */
const SCROLL_END_SLOP = 8;
/**
 * The floor `squeezeBy` cannot push the capped list below, in measured rows.
 * One WHOLE row: the first todo stays readable and its checkbox usable, and
 * the (shortened) fade under it still says there is more. This was 0.6 of a
 * row, on the theory that a clipped row reads as "more below" — but on a phone
 * too short for the page (iPhone SE, 640pt Androids) the sliver left under the
 * fade was unreadable, and the page had to scroll anyway. Anything the page
 * needs beyond this, the page scrolls for.
 */
const SQUEEZE_FLOOR_ROWS = 1;
/**
 * Stand-ins for the two empty-state heights until each has been measured: the
 * roomy card (tile, title, hint, 32pt of padding a side) and its one-line
 * compact form (built like a todo row). Both at the default font scale; a
 * measurement replaces each the first time its variant renders, and the page
 * re-decides on the corrected report.
 */
const ROOMY_EMPTY_HEIGHT_ESTIMATE = 157;
const COMPACT_EMPTY_HEIGHT_ESTIMATE = 57;

/**
 * What the capped list can give back to a page that is too short for it, in
 * points off its natural height. Reported through `onSqueezeCapacity` so the
 * page can pick, in one pass, how much to take from the list, whether to drop
 * a section of its own, and whether to collapse the list outright.
 */
export interface SqueezeCapacity {
  /**
   * Down to one row — or, on an empty day, down to the empty state's compact
   * line. `squeezeBy` takes this much at most.
   */
  toOneRow: number;
  /** Down to nothing: what `collapseList` gives back — the whole natural body. */
  toZero: number;
  /**
   * Whether `toOneRow` comes off point by point (rows: any `squeezeBy` up to
   * it is honored exactly) or all at once (the empty state swaps to its
   * compact line on ANY squeeze, so the page gets `toOneRow` back however
   * little it asked for).
   */
  granular: boolean;
}

interface TodoCalendarSectionProps {
  /** Test seam: pins "now" — its date AND its time of day. Defaults to the device clock, re-read by the minute. */
  todayOverride?: Date;
  /** Opens the full Planner screen. When absent the header shows no action. */
  onViewAll?: () => void;
  /** Section header copy. */
  title?: string;
  /**
   * Stretch to the parent's remaining height and scroll the todo list inside
   * the card instead of growing the page. Off by default so the section keeps
   * its original intrinsic-height behavior wherever the page still scrolls.
   */
  fill?: boolean;
  /**
   * Cap the todo list at N rows and scroll past them, leaving the card
   * intrinsically sized (chrome + up to N rows). The height of one row is
   * MEASURED, not assumed, so the cap follows the OS font scale. Ignored under
   * `fill`, which already bounds the list by the parent's height.
   */
  maxVisibleTodos?: number;
  /**
   * Points of height the page is asking this card to give back, because it is
   * shorter than the sections on it. Only meaningful alongside
   * `maxVisibleTodos`, which it turns from a fixed height into a MAXIMUM: the
   * visible list shrinks by this much (its own scroller and bottom fade taking
   * over) down to a floor of one whole row (SQUEEZE_FLOOR_ROWS); an empty day
   * swaps its roomy card for a one-line form on any squeeze at all. Whatever
   * it cannot absorb is the page's problem — its next rungs, then its scroll.
   */
  squeezeBy?: number;
  /**
   * The page's last rung: drop the list body entirely — rows or empty state —
   * leaving the date band, the strip and the overdue line. The band's "· N
   * due" still says what the day holds; View all is where the rows went.
   * Loading and error bodies are left alone (they have nothing to give).
   */
  collapseList?: boolean;
  /**
   * Reports what the body can give back (see SqueezeCapacity) whenever that
   * changes — a measured row, a different day, the empty state — and stays
   * silent while loading, under `fill`, and with no cap. Called from a layout
   * effect, so it lands before the layout events of the same commit.
   */
  onSqueezeCapacity?: (capacity: SqueezeCapacity) => void;
  /**
   * Tightens the section header's vertical padding — for the no-scroll home,
   * where header whitespace is height this planner does not get. Off by default.
   */
  denseHeader?: boolean;
}

export const TodoCalendarSection: React.FC<TodoCalendarSectionProps> = ({
  todayOverride,
  onViewAll,
  title = 'Planner',
  fill = false,
  maxVisibleTodos,
  squeezeBy = 0,
  collapseList = false,
  onSqueezeCapacity,
  denseHeader = false,
}) => {
  // "Now", by the minute (useMinuteClock): home is the stack's initial route and stays mounted for
  // days, so the date band and the overdue cutoff have to roll over at midnight, and a passed time
  // has to turn late, with nothing re-mounting.
  const now = useMinuteClock(todayOverride);
  const [selectedDate, setSelectedDate] = useState<Date>(now);
  const todayKey = toDateKey(now);
  const nowTimeKey = toTimeKey(now);
  // The visible week: the hook expands repeats over exactly these seven days.
  const weekDays = useMemo(() => getWeekDays(selectedDate), [selectedDate]);
  const {
    todos,
    overdueTodos,
    toggleTodo,
    isLoading,
    isError,
    hasData,
    refetch,
    saveTodo,
    removeTodo,
  } = useResidentTodos({ from: toDateKey(weekDays[0]), to: toDateKey(weekDays[6]), todayKey });

  const [formVisible, setFormVisible] = useState(false);
  const [formTodo, setFormTodo] = useState<ResidentTodo | null>(null);
  // Scroll-fade bookkeeping (either scrollable mode): the fade is shown only
  // while the list actually overflows AND is not already scrolled to the end.
  const [listViewportHeight, setListViewportHeight] = useState(0);
  const [listContentHeight, setListContentHeight] = useState(0);
  const [listAtEnd, setListAtEnd] = useState(false);
  // Starts true: a freshly rendered list sits at offset 0, so no rows are
  // hidden above the fold and the top fade must not render.
  const [listAtTop, setListAtTop] = useState(true);
  // Height of one rendered todo row, measured rather than assumed: the type
  // scale grows with the OS font setting, so a hardcoded row height would cut
  // the Nth row in half for anyone running large text.
  const [rowHeight, setRowHeight] = useState(0);
  // The two empty-state heights, measured as each variant renders (0 = not
  // yet). The ref names the variant on screen so a layout event lands on the
  // right one.
  const [emptyRoomyHeight, setEmptyRoomyHeight] = useState(0);
  const [emptyCompactHeight, setEmptyCompactHeight] = useState(0);
  const emptyVariantRef = useRef<'roomy' | 'compact'>('roomy');
  const listRef = useRef<ScrollView>(null);
  // The now line's scroll bookkeeping (spec 2026-10-07 §4.3): its offset in the list (from its own
  // layout), tagged with the row it was measured above so a stale one is never reused; one row's
  // height for the context row — measured in BOTH modes, while `rowHeight` above stays the cap's
  // alone; the day the resident took over scrolling on (null: following); and a nonce, so
  // re-tapping the selected day re-scrolls without a key change.
  const [nowLineMeasure, setNowLineMeasure] = useState<{ index: number; y: number } | null>(null);
  const [contextRowHeight, setContextRowHeight] = useState(0);
  const [stoppedOn, setStoppedOn] = useState<string | null>(null);
  const [followNonce, setFollowNonce] = useState(0);
  // Last viewport height as a ref too, so a layout pass can tell a shrink from
  // a growth without reading (and re-subscribing to) the state above.
  const listViewportRef = useRef(0);

  const type = useTypography();
  const space = useSpacing();
  const { colors: themeColors } = useThemeColors();
  const styles = useThemedStyles(({ colors, semantic }) =>
    StyleSheet.create({
      root: {
        // minHeight:0 lets the card actually shrink inside a flexed parent
        // instead of being floored by its content on web.
        flex: 1,
        minHeight: 0,
      },
      // No blanket `gap` on the card: the todo rows carry their own 16pt
      // vertical padding, so a card-level gap above the list DOUBLED the space
      // between the date band and the first row (32pt vs the 16pt row rhythm).
      // Each fixed chrome element below declares its own marginTop instead,
      // and the list gets none — its first row's padding IS the spacing.
      cardFill: {
        flex: 1,
        minHeight: 0,
      },
      headingRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: 44,
        // No hairline: solid rules fought the glass material. The band and
        // the strip are distinct shapes, and the 16pt beat separates them;
        // the chrome/list boundary is marked by the top scroll fade instead,
        // and only while rows are actually hidden under it.
      },
      stripWrap: {
        marginTop: space.lg,
      },
      heading: {
        ...type.cardTitleLarge,
        color: colors.text.primary,
        // Yield to "+ Add" on the row instead of pushing it off-screen.
        flexShrink: 1,
      },
      headingCount: {
        ...type.small,
        color: colors.text.muted,
      },
      // successText, not status.success: the latter is an INDICATOR green
      // (2.78:1 on the light card) and fails as ink.
      headingCountDone: {
        ...type.small,
        color: semantic.status.successText,
      },
      overdueRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: space.sm,
        // type.small + 4pt a side lands the box at ~24pt; the hitSlop on the
        // touchable is what reaches the 44pt floor.
        paddingVertical: space.xs,
        marginTop: space.lg,
      },
      overdueIcon: {
        // Warning ink on the alert glyph; the Planner's own overdue line flags itself with a red errorText dot.
        color: semantic.status.warning,
      },
      overdueText: {
        ...type.small,
        // secondary, not muted: muted is 4.48:1 on the light card.
        color: colors.text.secondary,
      },
      addText: {
        ...type.button,
        color: semantic.interactive.primaryHover, // AA-safe blue ink
      },
      // `fill` only. A capped list wraps in a plain (intrinsic) View instead —
      // either way the wrapper is the positioning context for the bottom scroll
      // fade, which has to overlay the scroller rather than take a row of its own.
      listArea: {
        flex: 1,
        minHeight: 0,
      },
      // `fill` only, likewise: a capped list is bounded by a measured maxHeight.
      listScroll: {
        flex: 1,
        minHeight: 0,
      },
      scrollFade: {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: -SCROLL_FADE_OVERSHOOT,
        // No height here: it follows the measured list viewport (fadeSizeStyle).
        // Also in the style, not just the prop: the web build swaps
        // react-native-linear-gradient for a mock that renders a raw <div>,
        // and a bare `pointerEvents` prop is not a DOM property — without the
        // CSS the fade would eat taps on the last row on web.
        pointerEvents: 'none',
      },
      // Mirror of scrollFade at the list's top edge: rows dissolve under the
      // week strip while scrolled instead of hard-clipping (the boundary the
      // old solid hairline used to draw). Same web-mock caveat as above.
      scrollFadeTop: {
        position: 'absolute',
        left: 0,
        right: 0,
        top: -SCROLL_FADE_OVERSHOOT,
        pointerEvents: 'none',
      },
      listScrollContent: {
        // Lets the empty state's flex:1 stretch to the whole scroll area so it
        // centers, while a full list still scrolls normally.
        flexGrow: 1,
      },
      // A row's targets reach CIRCLE_REACH into the gutter each side of it (the
      // toggle's left of the circle, the pencil's right of its glyph; a web row's
      // hover plate reaches space.sm, which fits inside), and the scroller clips
      // at its own edges. So the list area reaches CIRCLE_REACH into the card's
      // padding a side and pads its rows back by the same: rows stay exactly where
      // they were, and the scroll fades (left/right 0 of this area) still cover the
      // full width. Every platform: the targets need the gutter on native too.
      listGutter: {
        marginHorizontal: -CIRCLE_REACH,
      },
      listGutterContent: {
        paddingHorizontal: CIRCLE_REACH,
      },
      emptyWrap: {
        alignItems: 'center',
        paddingVertical: space['3xl'],
      },
      emptyWrapFill: {
        flex: 1,
        justifyContent: 'center',
      },
      emptyIconTile: {
        width: 44,
        height: 44,
        borderRadius: 14,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.activityIcon.bg,
        borderWidth: 1,
        borderColor: colors.activityIcon.border,
        marginBottom: space.md,
      },
      emptyText: {
        ...type.body,
        color: colors.text.muted,
      },
      emptyTitle: {
        ...type.cardTitle,
        color: colors.text.primary,
      },
      emptyHint: {
        ...type.caption,
        // secondary, not muted: muted is 4.48:1 on the light card, under the
        // 4.5:1 AA floor (same call TodoRow's date label makes).
        color: colors.text.secondary,
        paddingTop: space.xs,
      },
      // The empty state's one-line form, for a page that is squeezing: built
      // like a todo row (16pt a side, a 24pt slot where the checkbox goes) so
      // an empty day costs a row, not the ~157pt of the card above.
      emptyCompact: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: space.md,
        paddingVertical: space.lg,
      },
      emptyCompactIcon: {
        width: 24,
        height: 24,
        alignItems: 'center',
        justifyContent: 'center',
      },
      emptyCompactTitle: {
        ...type.body,
        color: colors.text.primary,
        flexShrink: 1,
      },
      emptyCompactHint: {
        ...type.caption,
        color: colors.text.secondary,
      },
      retryText: {
        ...type.button,
        color: semantic.interactive.primaryHover, // AA-safe blue ink
        paddingTop: space.sm,
      },
    }),
  );

  const groups = useMemo(() => groupTodosByDateKey(todos), [todos]);
  // Dots stand for REMAINING work, so the strip counts incomplete todos only:
  // a day whose items are all ticked off goes dotless. (The Planner screen's
  // MonthGrid map is filtered the same way so the two calendars agree.)
  const countsByKey = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const [key, list] of Object.entries(groups)) {
      counts[key] = list.filter(t => !t.completed).length;
    }
    return counts;
  }, [groups]);

  /**
   * THE MIDNIGHT RULE. "Today" is a moving anchor (the minute clock above), so
   * the day it names can change out from under a selection that has been
   * sitting still for hours. Which way that should go depends entirely on
   * whether the reader ever moved:
   *
   *   • Selection was still on the OLD today  → the reader never navigated;
   *     they were looking at "today" and expect to go on looking at today. Move
   *     the selection forward with the anchor, so the card rolls over quietly
   *     and no Today chip appears out of nowhere on a card nobody touched.
   *   • Selection was anywhere else           → the reader chose that day. It
   *     is still the day they chose, so leave it alone; the chip is already up,
   *     and it now points at the new today.
   *
   * Derived DURING RENDER, not in a passive effect, because the clock's new
   * minute and this carry-forward have to reach the screen in ONE commit.
   * A passive effect only runs once its commit is already on screen, so React
   * would paint a frame with `todayKey` rolled over and the selection still on
   * yesterday — the Today chip blinking in and straight back out, the band
   * naming yesterday, the pinned overdue line and the dots re-keying twice, on
   * a card nobody touched. That is exactly the transient the first rule above
   * exists to prevent. A set-state during render is different: React re-runs
   * this component (before rendering any child) and throws the first pass away,
   * so only the settled selection is ever committed.
   *
   * The previous anchor is the only thing that distinguishes the two cases,
   * hence the extra state: by the time this runs, `todayKey` is already the new
   * day's.
   */
  const [carriedTodayKey, setCarriedTodayKey] = useState(todayKey);
  if (carriedTodayKey !== todayKey) {
    setCarriedTodayKey(todayKey);
    if (toDateKey(selectedDate) === carriedTodayKey) setSelectedDate(now);
  }

  const selectedKey = toDateKey(selectedDate);
  const isOnToday = selectedKey === todayKey;
  const activeTodos = useMemo(
    () => sortTodos(groups[selectedKey] ?? []),
    [groups, selectedKey],
  );
  const dueCount = useMemo(
    () => activeTodos.filter(t => !t.completed).length,
    [activeTodos],
  );
  // Today's "now" line, and the band's spoken "next at …" (spec 2026-10-07).
  const nowIndex = useMemo(
    () => nowLineIndex(activeTodos, selectedKey, todayKey, nowTimeKey),
    [activeTodos, selectedKey, todayKey, nowTimeKey],
  );
  const nextTodo = useMemo(
    () => nextTimedTodo(activeTodos, selectedKey, todayKey, nowTimeKey),
    [activeTodos, selectedKey, todayKey, nowTimeKey],
  );
  // What the band says to a screen reader: its words without the "·", plus what's next.
  const bandLabel = `${formatDayHeading(selectedDate)}${
    dueCount > 0 ? `, ${dueCount} due` : activeTodos.length > 0 ? ', all done' : ''
  }${nextTodo?.dueTime ? `, next at ${formatTime(nextTodo.dueTime)}` : ''}`;
  // One row per repeat at most — see todoRecurrence overdueTodos.
  const overdueCount = overdueTodos.length;

  // Any day pick or page hands the list back to the line (it only scrolls on today).
  const handlePrevWeek = useCallback(() => {
    setSelectedDate(d => addDays(d, -7));
    setStoppedOn(null);
  }, []);
  const handleNextWeek = useCallback(() => {
    setSelectedDate(d => addDays(d, 7));
    setStoppedOn(null);
  }, []);
  const handleSelectDay = useCallback((d: Date) => {
    setSelectedDate(d);
    setStoppedOn(null);
    setFollowNonce(n => n + 1);
  }, []);

  const handleAdd = useCallback(() => {
    setFormTodo(null);
    setFormVisible(true);
  }, []);
  const handleEdit = useCallback((todo: ResidentTodo) => {
    setFormTodo(todo);
    setFormVisible(true);
  }, []);
  const handleCloseForm = useCallback(() => setFormVisible(false), []);
  // The modal owns try/catch; the hook maps values + scope to the right request.
  const handleSaveForm = useCallback(
    (values: TodoFormValues, scope?: EditScope) => saveTodo(formTodo, values, scope),
    [formTodo, saveTodo],
  );

  // A SHORTER viewport over unchanged content — which is exactly what a growing
  // `squeezeBy` does to a capped list — uncovers rows below the fold, so
  // "already at the end" can no longer be true; RN clamps the offset without
  // necessarily emitting a scroll event to re-derive the flag from (the same
  // Android gap the day-change reset covers). Only on a shrink: a viewport that
  // GREW can only make at-end more true, and re-arming there would flash the
  // fade back over a genuinely last row.
  const handleListLayout = useCallback((e: LayoutChangeEvent) => {
    const { height } = e.nativeEvent.layout;
    const shrank = listViewportRef.current - height > SCROLL_END_SLOP;
    listViewportRef.current = height;
    setListViewportHeight(height);
    if (shrank) setListAtEnd(false);
  }, []);
  // A new day's list is a new content height; drop the "already at the end"
  // flag so a short day that follows a scrolled-to-the-bottom long one does
  // not start out with the fade suppressed. The next scroll event re-derives it.
  const handleListContentSize = useCallback((_width: number, height: number) => {
    setListContentHeight(height);
    setListAtEnd(false);
  }, []);
  // A different day is a different list, but it is the SAME scroller: without
  // this it opens wherever the previous day was left, with its first rows cut
  // off above the fold. Instant, not animated:
  // the content swapped, so there is nothing to animate between. Re-seeding
  // `listAtEnd` covers Android too, which clamps a shorter day's offset
  // without emitting a scroll event to re-derive the flag from. On today, the follow effect
  // declared after this one overrides it in the same commit.
  useEffect(() => {
    listRef.current?.scrollTo({ y: 0, animated: false });
    setListAtEnd(false);
    setListAtTop(true);
  }, [selectedKey]);

  const handleNowLineLayout = useCallback(
    (e: LayoutChangeEvent) => {
      if (nowIndex === null) return;
      const { y } = e.nativeEvent.layout;
      setNowLineMeasure(prev => (prev && prev.index === nowIndex && prev.y === y ? prev : { index: nowIndex, y }));
    },
    [nowIndex],
  );
  // An offset measured while the line sat on another row is stale: don't scroll until the line
  // reports from where it is now (spec §7.4).
  const nowLineY = nowLineMeasure !== null && nowLineMeasure.index === nowIndex ? nowLineMeasure.y : null;
  // Where the list scrolls to keep the line in view: one row above it stays visible for context —
  // none when the list shows under two rows (the squeezed card), so its one row is the next todo.
  // Clamped to the scrollable range; null until the line, a row and the viewport have all reported.
  const nowScrollTarget = useMemo(() => {
    if (nowIndex === null || nowLineY === null || contextRowHeight <= 0 || listViewportHeight <= 0) {
      return null;
    }
    const context = listViewportHeight / contextRowHeight >= 2 ? contextRowHeight : 0;
    const maxOffset = Math.max(0, listContentHeight - listViewportHeight);
    return Math.min(Math.max(0, nowLineY - context), maxOffset);
  }, [nowIndex, nowLineY, contextRowHeight, listViewportHeight, listContentHeight]);
  const following = stoppedOn !== selectedKey;
  // The resident's own input hands them the list: a drag on native (never fired by our scrollTo)
  // and, since RNW 0.21's ScrollView fires no drag events (ScrollViewBase.js:128-130), a wheel or a
  // touch-move on web — plus keyboard focus moving into the rows (a tab).
  const stopFollowing = useCallback(() => setStoppedOn(selectedKey), [selectedKey]);
  const webListInputProps = useMemo(
    () => ({ onWheel: stopFollowing, onTouchMove: stopFollowing }) as object,
    [stopFollowing],
  );
  // Keyboard focus only (spec §4.3): RNW's pressables take focus on a click or a tap too, and ticking
  // a todo must not hand the list over on web when it doesn't on native. A browser without
  // :focus-visible throws on the selector — treat that as keyboard focus (the old behavior).
  const stopFollowingOnKeyboardFocus = useCallback(
    (e?: { target?: { matches?: (selector: string) => boolean } }) => {
      let keyboard = true;
      try {
        keyboard = e?.target?.matches?.(':focus-visible') ?? true;
      } catch {
        keyboard = true;
      }
      if (keyboard) stopFollowing();
    },
    [stopFollowing],
  );
  // Follow the line. Declared after the day-change reset, so in the commit that lands on today this
  // scroll wins. `selectedKey` and `followNonce` are triggers: a day change or a re-tap of today
  // re-scrolls even when the target number is unchanged.
  useEffect(() => {
    if (!following || nowScrollTarget === null) return;
    listRef.current?.scrollTo({ y: nowScrollTarget, animated: false });
  }, [following, nowScrollTarget, selectedKey, followNonce]);

  const handleListScroll = useCallback(
    ({ nativeEvent }: NativeSyntheticEvent<NativeScrollEvent>) => {
      const { contentOffset, contentSize, layoutMeasurement } = nativeEvent;
      setListAtEnd(
        contentOffset.y + layoutMeasurement.height >=
          contentSize.height - SCROLL_END_SLOP,
      );
      setListAtTop(contentOffset.y <= SCROLL_END_SLOP);
    },
    [],
  );

  // Which body is on screen. The cap governs the ROW LIST only — the loading,
  // error and empty cards keep the intrinsic presentation they already have.
  const showLoading = isLoading;
  const showError = !showLoading && isError && !hasData;
  const showEmpty = !showLoading && !showError && activeTodos.length === 0;
  const showRows = !showLoading && !showError && !showEmpty;

  // 0 = uncapped. `fill` wins outright: it already bounds the list by the
  // parent's height, and the two bounds would fight.
  const rowCap = !fill && maxVisibleTodos && maxVisibleTodos > 0 ? maxVisibleTodos : 0;
  // A squeeze only means anything against a cap; under `fill` (or with no cap
  // at all) there is no fixed bound for it to eat into.
  const squeeze = rowCap > 0 && squeezeBy > 0 ? squeezeBy : 0;
  // The first row's box: the cap's ruler (under a cap only, as before) and the now line's context
  // row (both modes). Ignore a zero-height pass; no-op on an unchanged height so measuring can never
  // loop.
  const handleRulerLayout = useCallback(
    (e: LayoutChangeEvent) => {
      const { height } = e.nativeEvent.layout;
      if (height <= 0) return;
      setContextRowHeight(prev => (prev === height ? prev : height));
      if (rowCap > 0) setRowHeight(prev => (prev === height ? prev : height));
    },
    [rowCap],
  );
  // The list's bound, in points. N whole rows and nothing else — a row's own
  // padding is inside the measured box, so the Nth
  // row ends flush with the cap — LESS whatever the page is squeezing out of
  // it, and never past the partial-row floor. 0 until the ruler has reported:
  // there is nothing to multiply yet, so the list renders unbounded for that
  // one frame (which lands before paint).
  const cappedMaxHeight = useMemo(() => {
    if (rowCap === 0 || rowHeight <= 0) return 0;
    const full = rowHeight * rowCap;
    if (squeeze <= 0) return full;
    return Math.min(full, Math.max(rowHeight * SQUEEZE_FLOOR_ROWS, full - squeeze));
  }, [rowCap, rowHeight, squeeze]);
  // Bound the list once the day's rows would out-grow that height — derived
  // from the heights themselves, not from the row COUNT, because a squeeze can
  // put the bound under a day that "fits" on paper. Before the ruler reports,
  // the count is the only signal there is. And any squeeze at all forces the
  // scrollable presentation: the page has been told the card gave height back,
  // so the rows it hid have to stay reachable (and the fade has to have a seam
  // to sit on) even on a two-todo day.
  const listOutgrowsBound =
    rowHeight > 0 && cappedMaxHeight > 0
      ? activeTodos.length * rowHeight > cappedMaxHeight
      : activeTodos.length > rowCap;
  const listCapped = rowCap > 0 && showRows && (squeeze > 0 || listOutgrowsBound);
  const listScrollable = fill || listCapped;
  const cappedListStyle = useMemo(
    () => (listCapped && cappedMaxHeight > 0 ? { maxHeight: cappedMaxHeight } : undefined),
    [listCapped, cappedMaxHeight],
  );
  // The empty state's one-line form, for a page that is squeezing: the page
  // asked for height back, and an empty day must not cost more than a full
  // one. Never under fill (no cap, so `squeeze` is 0 there).
  const compactEmpty = showEmpty && squeeze > 0;
  emptyVariantRef.current = compactEmpty ? 'compact' : 'roomy';
  // The page's last rung, for the bodies that HAVE height to give.
  const collapsed = rowCap > 0 && collapseList && (showRows || showEmpty);
  const handleEmptyLayout = useCallback((e: LayoutChangeEvent) => {
    const { height } = e.nativeEvent.layout;
    if (height <= 0) return;
    if (emptyVariantRef.current === 'compact') {
      setEmptyCompactHeight(prev => (prev === height ? prev : height));
    } else {
      setEmptyRoomyHeight(prev => (prev === height ? prev : height));
    }
  }, []);
  // What the body can give back (SqueezeCapacity), or null while unknown: no
  // cap, still loading, or rows not yet measured. Sized to the DAY, not the
  // cap — a one-row day has nothing above its one-row floor.
  const capacity = useMemo<SqueezeCapacity | null>(() => {
    if (rowCap === 0 || showLoading) return null;
    if (showError) return { toOneRow: 0, toZero: 0, granular: true };
    if (showEmpty) {
      const roomy = emptyRoomyHeight > 0 ? emptyRoomyHeight : ROOMY_EMPTY_HEIGHT_ESTIMATE;
      const compact =
        emptyCompactHeight > 0
          ? emptyCompactHeight
          : rowHeight > 0
            ? rowHeight
            : COMPACT_EMPTY_HEIGHT_ESTIMATE;
      return { toOneRow: Math.max(0, roomy - compact), toZero: roomy, granular: false };
    }
    if (rowHeight <= 0) return null;
    const visibleRows = Math.min(rowCap, activeTodos.length);
    return {
      toOneRow: rowHeight * Math.max(0, visibleRows - SQUEEZE_FLOOR_ROWS),
      toZero: rowHeight * visibleRows,
      granular: true,
    };
  }, [
    rowCap,
    showLoading,
    showError,
    showEmpty,
    emptyRoomyHeight,
    emptyCompactHeight,
    rowHeight,
    activeTodos.length,
  ]);
  const toOneRow = capacity?.toOneRow;
  const toZero = capacity?.toZero;
  const granular = capacity?.granular;
  // A layout effect, on the numbers rather than the object: it fires once per
  // real change and lands before the same commit's layout events reach the
  // page, so the page never pairs a fresh measurement with a stale report.
  useLayoutEffect(() => {
    if (toOneRow === undefined || toZero === undefined || granular === undefined) return;
    onSqueezeCapacity?.({ toOneRow, toZero, granular });
  }, [toOneRow, toZero, granular, onSqueezeCapacity]);

  // A scroller that unmounts on the same day (the page's ladder collapsing the list, or a short
  // day's squeeze lifting) keeps its last heights in state, so a remount would report the same
  // numbers and the follow effect would never re-run. Forget them on unmount — the remount's first
  // layout then re-triggers the follow — and re-seed the edge flags, as a day change does.
  const listMounted = listScrollable && !collapsed;
  useEffect(() => {
    if (listMounted) return;
    listViewportRef.current = 0;
    setListViewportHeight(0);
    setListContentHeight(0);
    setListAtEnd(false);
    setListAtTop(true);
  }, [listMounted]);

  // Only when there is genuinely more list below: an always-on fade veils the
  // last row on days that already fit.
  const showScrollFade = listContentHeight > listViewportHeight && !listAtEnd;
  const showTopScrollFade = listContentHeight > listViewportHeight && !listAtTop;
  // Both fades' depth, sized against the measured list viewport and never past
  // SCROLL_FADE_MAX_SHARE of it — full depth until the viewport has reported.
  // The overshoot rides on top, so the web seam stays closed at any size.
  const fadeSizeStyle = useMemo(() => {
    const fitted =
      listViewportHeight > 0
        ? Math.min(
            SCROLL_FADE_HEIGHT,
            Math.round(listViewportHeight * SCROLL_FADE_MAX_SHARE),
          )
        : SCROLL_FADE_HEIGHT;
    return { height: fitted + SCROLL_FADE_OVERSHOOT };
  }, [listViewportHeight]);
  // Derived from the live token, never written down: card.bg is near-white in
  // light and near-black in dark, so a literal would be wrong in one of them.
  // And NOT 'transparent' — that is rgba(0,0,0,0), which interpolates through
  // gray and smudges the light card.
  const scrollFadeColors = useMemo(
    () => [hexToRgba(themeColors.card.bg, 0), themeColors.card.bg],
    [themeColors.card.bg],
  );
  const topScrollFadeColors = useMemo(
    () => [themeColors.card.bg, hexToRgba(themeColors.card.bg, 0)],
    [themeColors.card.bg],
  );

  // Read at render, like the hover language: tests flip it before mounting.
  const isWeb = Platform.OS === 'web';
  const listBody = showLoading ? (
    <View style={[styles.emptyWrap, fill && styles.emptyWrapFill]}>
      <Text style={styles.emptyText}>Loading your todos…</Text>
    </View>
  ) : showError ? (
    // Only a failure with NOTHING cached takes over the body. RTK Query
    // also raises `isError` when a background LIST refetch fails while
    // cached rows are still in hand — and a rejected mutation fires its
    // `invalidatesTags`, so a failed offline toggle (optimistic undo →
    // refetch rejects) would otherwise replace a populated day with the
    // error card. With data, we keep showing it and let the row's own
    // rollback be the feedback.
    <View style={[styles.emptyWrap, fill && styles.emptyWrapFill]}>
      <Text style={styles.emptyText}>Couldn't load your todos.</Text>
      <Hoverable
        hover="link"
        onPress={refetch}
        accessibilityRole="button"
        accessibilityLabel="Retry loading todos"
        // 14 per side reaches the 44pt target around ~17pt text.
        hitSlop={{ top: 14, bottom: 14, left: 14, right: 14 }}
      >
        {({ textStyle }) => <Text style={[styles.retryText, textStyle]}>Retry</Text>}
      </Hoverable>
    </View>
  ) : showEmpty && compactEmpty ? (
    <View
      style={styles.emptyCompact}
      onLayout={handleEmptyLayout}
      testID="planner-empty-compact"
    >
      <View style={styles.emptyCompactIcon}>
        <CalendarIcon width={20} height={20} color={themeColors.text.muted} />
      </View>
      <Text style={styles.emptyCompactTitle} numberOfLines={1}>
        {isOnToday ? 'Nothing due today' : 'Nothing due this day'}
      </Text>
      <Text style={styles.emptyCompactHint} numberOfLines={1}>
        Tap + Add
      </Text>
    </View>
  ) : showEmpty ? (
    <View
      style={[styles.emptyWrap, fill && styles.emptyWrapFill]}
      // Measured only where it can be asked to shrink (a capped list).
      onLayout={rowCap > 0 ? handleEmptyLayout : undefined}
      testID="planner-empty"
    >
      <View style={styles.emptyIconTile}>
        <CalendarIcon width={20} height={20} color={themeColors.text.muted} />
      </View>
      <Text style={styles.emptyTitle}>
        {isOnToday ? 'Nothing due today' : 'Nothing due this day'}
      </Text>
      <Text style={styles.emptyHint}>Tap + Add to plan your day</Text>
    </View>
  ) : (
    <View testID="planner-rows" {...(isWeb ? ({ onFocus: stopFollowingOnKeyboardFocus } as object) : {})}>
      {insertNowLine(
        activeTodos.map((todo, index) => {
          const row = (
            <TodoRow
              key={todo.id}
              todo={todo}
              size="small"
              onToggle={toggleTodo}
              onEdit={handleEdit}
              late={isLate(todo, todayKey, nowTimeKey)}
            />
          );
          // The first row doubles as the ruler the cap multiplies: a plain
          // wrapper View, so onLayout reports that row's own box — its padding
          // included — without touching TodoRow's API.
          // A constant key keeps the wrapper (and its measurement) in place as
          // the days change under it. The now line splices in BESIDE it, never
          // inside, so the ruler stays on the first todo row.
          return index === 0 ? (
            <View
              key="todo-row-ruler"
              onLayout={handleRulerLayout}
            >
              {row}
            </View>
          ) : (
            row
          );
        }),
        nowIndex,
        handleNowLineLayout,
      )}
    </View>
  );

  return (
    <View style={fill ? styles.root : undefined}>
      <SectionHeader
        title={title}
        actionLabel={onViewAll ? 'View all' : undefined}
        onAction={onViewAll}
        dense={denseHeader}
      />
      {/* Either bounded mode is the sized-to-fit page, where the card's default
          24pt inset is 16pt of page height the planner does not get back. 16
          (space.lg) is what the mock draws there; a plain intrinsic host, on a
          page that scrolls anyway, keeps the roomier default. */}
      <GlassCard
        padding={fill || rowCap > 0 ? space.lg : undefined}
        style={fill ? styles.cardFill : undefined}
      >
        {/* The date band leads the card: it names the selected day and holds
            the actions; the strip below it is how you change that day. */}
        <View style={styles.headingRow}>
          <Text style={styles.heading} accessibilityLabel={bandLabel}>
            {formatDayHeading(selectedDate)}
            {/* A day with rows but nothing left open earns the win; a day with
                no rows at all says nothing (the empty state below speaks). */}
            {dueCount > 0 ? (
              <Text style={styles.headingCount}>{` · ${dueCount} due`}</Text>
            ) : activeTodos.length > 0 ? (
              <Text style={styles.headingCountDone}>{' · all done'}</Text>
            ) : null}
          </Text>
          <Hoverable
            hover="link"
            onPress={handleAdd}
            // 14 per side reaches the 44pt target around ~17pt text (TodoRow's
            // pencil precedent).
            hitSlop={{ top: 14, bottom: 14, left: 14, right: 14 }}
            accessibilityRole="button"
            accessibilityLabel="Add todo"
          >
            {({ textStyle }) => <Text style={[styles.addText, textStyle]}>+ Add</Text>}
          </Hoverable>
        </View>
        {/* Always on, under the date band: the strip IS the mode switch now —
            and the way back to today (its cell in the current week, or paging
            back to it). */}
        <View style={styles.stripWrap}>
          <WeekStrip
            weekDays={weekDays}
            selectedKey={selectedKey}
            todayKey={todayKey}
            countsByKey={countsByKey}
            onSelectDay={handleSelectDay}
            onPrevWeek={handlePrevWeek}
            onNextWeek={handleNextWeek}
          />
        </View>
        {/* Pinned under the week strip, OUTSIDE the scroller: stragglers are
            exactly what must not scroll away. Only offered when there is
            somewhere to send the tap; nothing overdue renders nothing, so it
            costs zero height on a clean day. */}
        {overdueCount > 0 && onViewAll ? (
          <Hoverable
            hover="row"
            // A compact line (type.small, 4pt padding): the plate also
            // reaches space.xs above and below so the label keeps 8pt of air.
            bleed={{ x: space.sm, y: space.xs }}
            style={styles.overdueRow}
            onPress={onViewAll}
            // Reach 44pt mostly UPWARD into the row's own 16pt marginTop (dead
            // space, uncontested — the strip above is an earlier sibling). The
            // zone below now belongs to the first todo row's top padding, and
            // the row wins that overlap as the later sibling, so a big bottom
            // slop would be a lie. Vertical only: the row spans the card.
            hitSlop={{ top: 14, bottom: 4 }}
            accessibilityRole="button"
            accessibilityLabel={`${overdueCount} overdue`}
            accessibilityHint="Opens the planner"
          >
            <Icon
              name="alert-circle-outline"
              // 16, not the Planner card's 18: this line sets in type.small.
              size={16}
              style={styles.overdueIcon}
            />
            <Text style={styles.overdueText}>{`${overdueCount} overdue`}</Text>
          </Hoverable>
        ) : null}
        {collapsed ? null : listMounted ? (
          // `fill` stretches this area into the parent's leftover height; a
          // capped list sizes it to the scroller, which stops at N rows less
          // any squeeze. Either way it is the positioning context the bottom
          // fade overlays.
          <View style={[fill ? styles.listArea : undefined, styles.listGutter]}>
            <ScrollView
              ref={listRef}
              style={fill ? styles.listScroll : cappedListStyle}
              contentContainerStyle={[styles.listScrollContent, styles.listGutterContent]}
              showsVerticalScrollIndicator={false}
              // Android only, and only relevant on a short viewport: the home
              // page keeps a ScrollView of its own as an overflow fallback, and
              // without this the parent swallows this list's gestures when it is.
              nestedScrollEnabled
              onLayout={handleListLayout}
              onContentSizeChange={handleListContentSize}
              onScroll={handleListScroll}
              onScrollBeginDrag={stopFollowing}
              {...(isWeb ? webListInputProps : {})}
              scrollEventThrottle={16}
            >
              {listBody}
            </ScrollView>
            {showTopScrollFade ? (
              <LinearGradient
                pointerEvents="none"
                colors={topScrollFadeColors}
                style={[styles.scrollFadeTop, fadeSizeStyle]}
              />
            ) : null}
            {showScrollFade ? (
              <LinearGradient
                pointerEvents="none"
                colors={scrollFadeColors}
                style={[styles.scrollFade, fadeSizeStyle]}
              />
            ) : null}
          </View>
        ) : (
          listBody
        )}
      </GlassCard>
      {/* Outside the scroll view on purpose: it is a full-screen overlay, and
          nesting it under a scroller clips/scrolls the sheet. */}
      <TodoFormModal
        visible={formVisible}
        todo={formTodo}
        initialDateKey={selectedKey}
        onSave={handleSaveForm}
        onDelete={removeTodo}
        onClose={handleCloseForm}
        now={now}
      />
    </View>
  );
};

TodoCalendarSection.displayName = 'TodoCalendarSection';
